import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './Common.css';
import OrderCheck from './Body/OrderCheck';
import StockCheck from './Body/StockCheck';
import ProductSeriesPage from './Body/ProductSeriesPage';
import ProductCharacterPage from './Body/ProductCharacterPage';

import pinyin from 'pinyin';
import HomePage from './Body/HomePage';
import Header from './Header/Header';
import Footer from './Footer/Footer';
// 新增 Cart 的 import
import Cart from './Body/CartItem/Cart';
import { getApiUrl } from './config/api';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SystemActivityPage from './Body/SystemActivityPage';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import createQueryClient from './config/queryClient';

const queryClient = createQueryClient();

function App() {
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false);
  const [characterDropdownOpen, setCharacterDropdownOpen] = useState(false);
  const [systemActivityDropdownOpen, setSystemActivityDropdownOpen] = useState(false);
  const seriesDropdownRef = useRef(null);
  const characterDropdownRef = useRef(null);
  const systemActivityDropdownRef = useRef(null);
  const sidebarRef = useRef(null);
  // 移除未使用的 url 變數
  const characterOptions = {
    '小可愛': 'chiikawa',
    '小八': 'hachiware',
    '兔兔': 'usagi',
    '小桃': 'momotaro',
    '栗子': 'kuri',
    '師傅': 'raku',
    '西薩': 'sisa',
    '古本屋': 'furu',
    '那孩子': 'anoko',
    '小甲蟲': 'kabutomushi',
    '哥布林': 'goblin',
    '鎧甲人': 'yoroisan',
    '白熊': 'kuma',
    '巴哥': 'pug',
    '三隻': 'three',
    '全員': 'all',
    '可樂餅': 'koroke',
    '土龍': 'tora'
  };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const { data: seriesOptions = [] } = useQuery({
    queryKey: ['seriesOptions'],
    queryFn: async () => {
      const response = await fetch(getApiUrl('/orders/get_all_product_series'));
      const data = await response.json();
      return data.data || [];
    },
    staleTime: 60 * 60 * 1000 // 1小時
  });

  const { data: systemActivityOptions = [] } = useQuery({
    queryKey: ['systemActivityOptions'],
    queryFn: async () => {
      const response = await fetch(getApiUrl('/orders/get_all_product_system_activity'));
      const data = await response.json();
      return data.data || [];
    },
    staleTime: 60 * 60 * 1000 // 1小時
  });

  useEffect(() => {
    function handleResize() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android/i.test(userAgent) || /iPad|iPhone|iPod/.test(userAgent);
      setIsMobile(isMobileDevice || window.innerWidth < 900);
    }
    window.addEventListener('resize', handleResize);
    handleResize(); // 初始化
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    function setHeaderHeight() {
      let headerHeight = 0;
      const headerWrapper = document.getElementById('header-fixed-wrapper');
      if (headerWrapper) {
        headerHeight = headerWrapper.offsetHeight;
      }
      document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
    }
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight);
    return () => window.removeEventListener('resize', setHeaderHeight);
  }, []);

  useEffect(() => {
    function adjustHeader() {
      // 桌面
      const titleHeader = document.querySelector('.fixed-header.title-header');
      const headerCenter = document.querySelector('.fixed-header.header-center');
      if (titleHeader && headerCenter) {
        headerCenter.style.top = titleHeader.offsetHeight + 'px';
      }
    }
    adjustHeader();
    window.addEventListener('resize', adjustHeader);
    return () => window.removeEventListener('resize', adjustHeader);
  }, []);

  const convertToPinyin = (chinese) => {
    return pinyin(chinese, { style: pinyin.STYLE_NORMAL }).join('').replace(/\//g, '_');
  };

  const toggleSeriesDropdown = () => {
    setSeriesDropdownOpen(prev => !prev);
    setSystemActivityDropdownOpen(false);
    setCharacterDropdownOpen(false);
  };

  const toggleCharacterDropdown = () => {
    setCharacterDropdownOpen(prev => !prev);
    setSystemActivityDropdownOpen(false);
    setSeriesDropdownOpen(false);
  };

  const toggleSystemActivityDropdown = () => {
    setSystemActivityDropdownOpen(prev => !prev);
    setSeriesDropdownOpen(false);
    setCharacterDropdownOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(prevState => !prevState);
    if (sidebarOpen) {
      setSeriesDropdownOpen(false);
      setCharacterDropdownOpen(false);
      setSystemActivityDropdownOpen(false);
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className={`App ${isMobile ? 'mobile' : 'desktop'}`}>
          <Header
            isMobile={isMobile}
            sidebarOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
            sidebarRef={sidebarRef}
            seriesDropdownOpen={seriesDropdownOpen}
            characterDropdownOpen={characterDropdownOpen}
            systemActivityDropdownOpen={systemActivityDropdownOpen}
            toggleSeriesDropdown={toggleSeriesDropdown}
            toggleCharacterDropdown={toggleCharacterDropdown}
            toggleSystemActivityDropdown={toggleSystemActivityDropdown}
            seriesOptions={seriesOptions}
            characterOptions={characterOptions}
            systemActivityOptions={systemActivityOptions}
            convertToPinyin={convertToPinyin}
            seriesDropdownRef={seriesDropdownRef}
            characterDropdownRef={characterDropdownRef}
            systemActivityDropdownRef={systemActivityDropdownRef}
            setSystemActivityDropdownOpen={setSystemActivityDropdownOpen}
            setSeriesDropdownOpen={setSeriesDropdownOpen}
            setCharacterDropdownOpen={setCharacterDropdownOpen}
          />
          <div className="main-content">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/order" element={<OrderCheck />} />
              <Route path="/stock" element={<StockCheck />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/products/:series" element={<ProductSeriesPage seriesOptions={seriesOptions} />} />
              <Route path="/products_character/:character" element={<ProductCharacterPage />} />
              <Route path="/series/:series" element={<ProductSeriesPage seriesOptions={seriesOptions} />} />
              <Route path="/activity/:activity" element={<SystemActivityPage systemActivityOptions={systemActivityOptions} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
